import React, {useEffect, useImperativeHandle, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Image,
    Text,
    useBreakpointValue,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getUrl} from '../../../utils/cloudinary-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useTagManager from '../../../hooks/use-tag-manager'
import {useGlobaleContext} from '../../../contexts/globale'
import {isLimitReached} from '../../../utils/product-utils'

const MirelzButtons = ({
    productId,
    productName,
    isAddToBagEnabled = true,
    initialMobileImageIndex = 0,
    imageIndexControllerRef,
    basket = {},
    product = {},
    preorderable = false,
    category
}) => {
    const location = useLocation()
    const mirelz360Ref = useRef(null)
    const {onClose: onCloseCompleteTheLookModal} = useDisclosure()

    const mirelzVtoRef = useRef(null)
    const [mobileImageIndex, setMobileImageIndex] = useState(initialMobileImageIndex)
    useImperativeHandle(imageIndexControllerRef, () => ({setMobileImageIndex}), [])
    const limitReached = isLimitReached(productId, basket)
    const tagManager = useTagManager()
    const buttonsMainRef = useRef(null)
    const {countryISO} = useGlobaleContext()
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const {
        app: {commerceAPI}
    } = getConfig()

    const {mirelz} = commerceAPI
    const {config} = mirelz || {}

    var mirelzEnable = false
    var mz360Enabled = false
    var mirelzAvailableCountries = null

    try {
        const jsonConfig = JSON.parse(config)
        mirelzEnable = jsonConfig.enableVTO
        mz360Enabled = jsonConfig.enable360
        mirelzAvailableCountries = jsonConfig.availableCountries
    } catch (e) {
        console.log(e.message)
    }

    useEffect(() => {
        let controlTryOnInterval
        let attempts = 0
        if (mirelzVtoRef?.current && mirelzEnable) {
            // When a user selects a different swatch, ensure that the availability of Mirelz is revalidated.
            controlTryOnInterval = window.setInterval(function () {
                attempts += 1
                /** Not a reactJS practice - We need to make sure __esModule is there before we execute this  **/
                if (window?.MirelzApp?.__esModule) {
                    window.MirelzApp.isTryOnEnabled(
                        {
                            itemId: productId,
                            showLogs: true,
                            sessionId: window.SESSION_ID
                        },
                        function (enabled) {
                            if (enabled) {
                                mirelzVtoRef.current?.classList?.remove('hide-element-try-on')
                            } else {
                                mirelzVtoRef.current?.classList?.add('hide-element-try-on')
                            }
                        }
                    )
                    window.clearInterval(controlTryOnInterval)
                } else if (attempts >= 50) {
                    window.clearInterval(controlTryOnInterval)
                }
            }, 200)
        }

        return () => {
            if (controlTryOnInterval !== undefined) {
                window.clearInterval(controlTryOnInterval)
            }
        }
    }, [productId, mirelzVtoRef?.current, mirelzEnable])

    useEffect(() => {
        let control360Interval
        let attempts = 0
        // When a user selects a different swatch, ensure that the availability of Mirelz is revalidated.
        if (mirelz360Ref?.current && mirelzEnable && mz360Enabled) {
            control360Interval = window.setInterval(function () {
                attempts += 1
                /** Not a reactJS practice - We need to make sure __esModule is there before we execute this  **/
                if (window.Mirelz360?.__esModule) {
                    window.Mirelz360.is360Enabled(
                        {
                            itemId: productId,
                            showLogs: true
                        },
                        function (enabled) {
                            if (enabled) {
                                mirelz360Ref?.current?.classList.remove('hide-element-360')
                            } else {
                                mirelz360Ref?.current?.classList.add('hide-element-360')
                            }
                        }
                    )
                    window.clearInterval(control360Interval)
                } else if (attempts >= 50) {
                    window.clearInterval(control360Interval)
                }
            }, 200)
        }

        return () => {
            if (control360Interval !== undefined) {
                window.clearInterval(control360Interval)
            }
        }
    }, [productId, mirelz360Ref?.current, mirelzEnable])

    useEffect(() => {
        const handleClick = (e) => {
            const className = e?.target?.className
            if (typeof className === 'string') {
                if (className.includes('mz-primary-action')) {
                    tagManager.virtualTryOnConsent('consent_granted', productName, category)
                } else if (className.includes('mz-secondary-action')) {
                    tagManager.virtualTryOnConsent('consent_denied', productName, category)
                }
            }
        }

        document.addEventListener('click', handleClick)

        return () => {
            document.removeEventListener('click', handleClick)
        }
    }, [])

    useEffect(() => {
        const mirelzCloseModal = (e) => {
            const target = e.target instanceof Element ? e.target : null
            if (!target) return

            if (
                target.closest('.mz-modal-popup') &&
                (target.className?.indexOf('mz-close-action') !== -1 ||
                    target.className?.indexOf('mz-close-button') !== -1)
            ) {
                tagManager.virtualTryOnClose(productName, category)
            }

            if (target.closest('.mirelz-modal-360 .mz-close-button')) {
                tagManager.mirelz360Close(productName)
            }
        }

        document.addEventListener('click', mirelzCloseModal)

        return () => {
            document.removeEventListener('click', mirelzCloseModal)
        }
    }, [])

    useEffect(() => {
        if ((!mobileImageIndex || isDesktop) && window.Mirelz360?.__esModule) {
            window.Mirelz360.is360Enabled(
                {
                    itemId: productId,
                    showLogs: true
                },
                function (enabled) {
                    if (enabled) {
                        mirelz360Ref?.current?.classList.remove('hide-element-360')
                    } else {
                        mirelz360Ref?.current?.classList.add('hide-element-360')
                    }
                }
            )
        }
    }, [mobileImageIndex, isDesktop])

    const showMirelzButtons =
        mirelzAvailableCountries &&
        countryISO !== null &&
        mirelzEnable &&
        mirelzAvailableCountries.indexOf(countryISO) !== -1

    // Close add-to-cart drawer on route change
    useEffect(() => {
        onCloseCompleteTheLookModal()
    }, [location?.pathname])

    return showMirelzButtons ||
        (product?.c_showCompleteTheLook &&
            countryISO !== null &&
            product?.c_completeTheLookIDs?.length) ? (
        <Box
            display={{
                base: 'grid',
                lg: 'inline-flex'
            }}
            className="mirelz-parent"
            gap="15px"
            data-enabled={isAddToBagEnabled}
            ref={buttonsMainRef}
            position="absolute"
            top={{base: 'unset', lg: '19.96'}}
            right={{base: '9.76', lg: '18.68'}}
            bottom={{base: '15px', lg: 'unset'}}
            zIndex="2"
        >
            <Box
                className="mirelz360-wrapper hide-element-360"
                ref={mirelz360Ref}
                marginLeft="auto"
                display={{
                    base: showMirelzButtons && !mobileImageIndex && mz360Enabled ? 'block' : 'none',
                    lg: showMirelzButtons && mz360Enabled ? 'block' : 'none'
                }}
                onClick={() => {
                    tagManager.mirelz360Open(productName)
                    const request =
                        preorderable || limitReached
                            ? {
                                  itemId: productId,
                                  isAddToBagEnabled: false,
                                  showLogs: false
                              }
                            : {
                                  itemId: productId,
                                  showLogs: false,
                                  isAddToBagEnabled:
                                      buttonsMainRef.current.getAttribute('data-enabled') ===
                                      'true',
                                  addToBag: async (productIDs) => {
                                      window.mzProductIDs = productIDs
                                      document.querySelector('.mirelz-hidden-btn-mz360')?.click()
                                  }
                              }
                    window.Mirelz360.load360(request)
                }}
                borderRadius="51.347px"
                backgroundColor="white"
                transition="all, 0.3s ease"
                _hover={{
                    backgroundColor: 'gray.425'
                }}
                padding="0px 24px"
                width="fit-content"
                height="30px"
                backdropFilter="blur(3.469388008117676px)"
                cursor="pointer"
            >
                <Image
                    src={getUrl('UIImages/images/yr-pdp-360.svg')}
                    alt="360 view"
                    className="mirelz360-wrapper"
                    height="30px"
                    width="30px"
                />
            </Box>
            <Box
                className="mirelz-try-it-on hide-element-try-on"
                ref={mirelzVtoRef}
                onClick={() => {
                    tagManager.virtualTryOnClick(productName, category)
                    const request =
                        preorderable || limitReached
                            ? {
                                  itemId: productId,
                                  isAddToBagEnabled: false,
                                  sessionId: window.SESSION_ID
                              }
                            : {
                                  itemId: productId,
                                  sessionId: window.SESSION_ID,
                                  isAddToBagEnabled:
                                      buttonsMainRef.current.getAttribute('data-enabled') ===
                                      'true',
                                  addToBag: async (productIDs) => {
                                      window.mzProductIDs = productIDs
                                      document
                                          .querySelector('.mirelz-hidden-btn-mzDefault')
                                          ?.click()
                                  }
                              }
                    window.MirelzApp.loadTryOn(request)
                }}
                borderRadius="51.347px"
                backgroundColor="white"
                borderWidth="0.694px"
                borderStyle="solid"
                borderColor="gray.450"
                padding="12px 24px"
                width="fit-content"
                display={showMirelzButtons ? 'inline-flex' : 'none'}
                transition="all, 0.3s ease"
                _hover={{
                    backgroundColor: 'gray.425'
                }}
                gap="6.4px"
                alignItems="center"
                height="30px"
                backdropFilter="blur(3.469388008117676px)"
                cursor="pointer"
            >
                <Image
                    src={getUrl('UIImages/images/yr-pdp-camera.svg')}
                    alt="Try It On"
                    className="mirelz-try-it-on"
                    height="15px"
                    width="15px"
                />
                <Text
                    color="black.100"
                    className="mirelz-try-it-on"
                    fontWeight="400"
                    fontSize="11.102px"
                    lineHeight="17.347px"
                    letterSpacing="0.056px"
                >
                    <FormattedMessage id="try.it.on" defaultMessage="Try it On" />
                </Text>
            </Box>
        </Box>
    ) : (
        <></>
    )
}

MirelzButtons.propTypes = {
    productId: PropTypes.string,
    productName: PropTypes.string,
    isAddToBagEnabled: PropTypes.bool,
    initialMobileImageIndex: PropTypes.number,
    imageIndexControllerRef: PropTypes.shape({current: PropTypes.any}),
    basket: PropTypes.object,
    product: PropTypes.object,
    preorderable: PropTypes.bool,
    category: PropTypes.string
}

export default MirelzButtons
